import { PRIVACY_POLICIES, type PrivacyPolicy, type PrivacyRegime } from '@krevya/shared';
import { SESSION_ONLY_KEYS } from './storage-inventory.js';

/**
 * The visitor's privacy choice, and the gate everything optional goes through.
 *
 * This module is the one place that decides what may run. It combines three
 * things: where the visitor is (the privacy model `GET /api/geo` answers,
 * see `region.ts` and `@krevya/shared`'s `privacy.ts`), what they chose (the
 * consent record), and their browser's Global Privacy Control signal.
 *
 *  - In the strict model (the EEA, Switzerland, Turkey, the UK and every
 *    country not named otherwise) nothing that is not strictly necessary may
 *    be stored on the device or run in the page until the visitor has said yes
 *    to it, saying no has to be as easy as saying yes, and the choice has to
 *    be changeable later. GDPR and KVKK ask for exactly this.
 *  - In the US model there is no banner: preferences are kept and basic
 *    analytics runs by default, session replay never runs, and "Privacy
 *    choices" turns either off. An opt-out is saved as a consent record and
 *    wins over the default. GPC counts as an analytics opt-out.
 *  - Until the model is known, the page behaves strictly and shows no banner,
 *    so a US visitor never sees one flash and a strict visitor never has
 *    anything run early.
 *
 * The rule for the rest of the code base:
 *
 *  - A preference remembered across visits (a folded rail, a dismissed tip, a
 *    "seen it" flag, the page language) goes through `preferenceStorage()`,
 *    never straight to `localStorage`. Without consent it lands in
 *    sessionStorage (or memory), which is gone when the tab closes. Every key
 *    is listed in `storage-inventory.ts`.
 *  - An analytics or marketing script is loaded with `loadScript(category, …)`
 *    or started inside `whenAllowed(category, …)`, never with a bare
 *    `<script>` tag in index.html.
 *
 * The choice lives in a first-party cookie, `krevya_consent`, for twelve
 * months, and is mirrored to localStorage. The mirror is there because Safari
 * caps any cookie written from script at seven days; without it a Safari
 * visitor would be asked again every week.
 */

export const CONSENT_VERSION = 1;
export const CONSENT_COOKIE = 'krevya_consent';
/** Where the copy of the choice lives in localStorage. */
export const CONSENT_MIRROR_KEY = 'krevya_consent';
/** Twelve months, which is as long as a choice should be trusted before asking again. */
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export type OptionalCategory = 'preferences' | 'analytics' | 'marketing';
export type ConsentCategory = 'necessary' | OptionalCategory;
export type ConsentChoice = Record<OptionalCategory, boolean>;
export type ConsentState = { v: number; necessary: true; at: string } & ConsentChoice;

export const OPTIONAL_CATEGORIES: readonly OptionalCategory[] = [
  'preferences',
  'analytics',
  'marketing',
];

/**
 * The categories the preferences dialog offers. Marketing stays in the record
 * and the types so it can come back the day a marketing tag exists, but there
 * is none today, so it is not offered and nothing ever turns it on.
 */
export const OFFERED_CATEGORIES: readonly OptionalCategory[] = ['preferences', 'analytics'];

/**
 * Where the visitor is, as far as privacy goes: the model, whether their
 * browser sends GPC, and the country (null when unknown), which analytics uses
 * to decide whether it may join events to a signed-in user. Held in memory
 * only, never stored.
 */
export type PrivacyRegion = { regime: PrivacyRegime; gpc: boolean; country: string | null };

/** What the banner and the dialog render from; a new object whenever anything changes. */
export type ConsentSnapshot = {
  record: ConsentState | null;
  /** Null until `/api/geo` has answered (or failed). */
  region: PrivacyRegion | null;
};

/**
 * The localStorage keys that are strictly necessary and stay whatever the
 * answer: only the consent record itself. Every other `krevya.*` key in
 * localStorage is a preference (the page language included), which is what
 * lets a withdrawal clear them without a list that has to be kept in step
 * with the code.
 */
export const NECESSARY_LOCAL_KEYS: readonly string[] = [CONSENT_MIRROR_KEY];

const PREFERENCE_PREFIX = 'krevya.';

/**
 * The sessionStorage keys that are necessary and tab-scoped by design (a promo
 * code carried to sign-up, the page to return to after Shopify, a gate or a
 * bar already answered in this tab). They never move to localStorage when
 * preferences are allowed, because they are not preferences.
 */
export { SESSION_ONLY_KEYS };

/** Reads a stored record; anything malformed, from another version, or too old counts as no answer. */
export function parseConsent(raw: string | null | undefined, now: number): ConsentState | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const record = parsed as Record<string, unknown>;
  if (record['v'] !== CONSENT_VERSION) return null;
  if (typeof record['at'] !== 'string') return null;
  const at = Date.parse(record['at']);
  if (!Number.isFinite(at)) return null;
  // An answer from the future is a clock that was wrong, an old one has expired.
  if (at > now + 24 * 60 * 60 * 1000 || now - at > CONSENT_MAX_AGE_MS) return null;
  const choice: Partial<ConsentChoice> = {};
  for (const category of OPTIONAL_CATEGORIES) {
    const value = record[category];
    if (typeof value !== 'boolean') return null;
    choice[category] = value;
  }
  return { v: CONSENT_VERSION, necessary: true, at: record['at'], ...(choice as ConsentChoice) };
}

export function serializeConsent(state: ConsentState): string {
  return JSON.stringify({
    v: state.v,
    necessary: true,
    preferences: state.preferences,
    analytics: state.analytics,
    marketing: state.marketing,
    at: state.at,
  });
}

/** The value of one cookie in a `document.cookie` string. */
export function readCookieValue(cookies: string, name: string): string | null {
  for (const part of cookies.split(';')) {
    const index = part.indexOf('=');
    if (index < 0) continue;
    if (part.slice(0, index).trim() !== name) continue;
    try {
      return decodeURIComponent(part.slice(index + 1).trim());
    } catch {
      return null;
    }
  }
  return null;
}

type KeyValue = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

/** What the store needs from the browser, handed in so the logic can be tested without one. */
export type ConsentEnv = {
  readCookie: () => string | null;
  writeCookie: (value: string, maxAgeSeconds: number) => void;
  /** localStorage, or null where it cannot be touched. */
  local: () => KeyValue | null;
  /** sessionStorage, or null where it cannot be touched. */
  session: () => KeyValue | null;
  now: () => number;
  /** Called when a category whose code already ran is withdrawn: only a reload unloads a script. */
  reload: () => void;
};

export type ConsentStore = ReturnType<typeof createConsentStore>;

/** A Storage that lives as long as the page, for a browser that refuses both real ones. */
function memoryStorage(): KeyValue {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => void map.set(key, String(value)),
    removeItem: (key) => void map.delete(key),
    key: (index) => [...map.keys()][index] ?? null,
    get length() {
      return map.size;
    },
  };
}

export function createConsentStore(env: ConsentEnv) {
  let cached: ConsentState | null | undefined;
  let region: PrivacyRegion | null = null;
  let snapshot: ConsentSnapshot | null = null;
  const listeners = new Set<(state: ConsentState | null) => void>();
  const openListeners = new Set<() => void>();
  /** Categories whose gated code has already run in this page. */
  const ran = new Set<OptionalCategory>();
  /** Where preferences go when neither real storage can be touched. */
  let memory: KeyValue | null = null;

  function read(): ConsentState | null {
    const now = env.now();
    const fromCookie = parseConsent(safe(env.readCookie), now);
    if (fromCookie) return fromCookie;
    const local = safe(env.local);
    const fromMirror = parseConsent(safe(() => local?.getItem(CONSENT_MIRROR_KEY)), now);
    if (fromMirror) {
      // The cookie was capped or lost and the mirror still holds the answer:
      // put it back for whatever is left of the twelve months.
      const left = CONSENT_MAX_AGE_MS - (now - Date.parse(fromMirror.at));
      safe(() => env.writeCookie(serializeConsent(fromMirror), Math.floor(left / 1000)));
    }
    return fromMirror;
  }

  function get(): ConsentState | null {
    if (cached === undefined) cached = read();
    return cached;
  }

  /** The policy in force, or null while the region is not known yet. */
  function policy(): PrivacyPolicy | null {
    return region ? PRIVACY_POLICIES[region.regime] : null;
  }

  /**
   * Whether a category may run right now.
   *
   * A saved answer decides, with two exceptions: marketing never runs under
   * GPC, and analytics waits for the region even with a yes on record, because
   * whether session replay may run depends on it. Without an answer the
   * region's defaults decide, and until the region is known nothing optional
   * runs at all.
   */
  function allows(category: ConsentCategory): boolean {
    if (category === 'necessary') return true;
    if (category === 'marketing' && region?.gpc) return false;
    const record = get();
    if (record) {
      if (category !== 'preferences' && !region) return false;
      return record[category];
    }
    const rules = policy();
    if (!rules || !region) return false;
    if (category === 'preferences') return rules.preferencesByDefault;
    if (category === 'analytics') return rules.analyticsByDefault && !region.gpc;
    return false;
  }

  /** The banner is up: the region asks first and nothing has been answered. */
  function needsAnswer(): boolean {
    return policy()?.askFirst === true && get() === null;
  }

  /**
   * Session replay may run: only where the policy allows it at all, and only
   * on an explicit yes to analytics. Never on a default.
   */
  function replayAllowed(): boolean {
    const rules = policy();
    if (!rules || rules.sessionReplay !== 'after-consent') return false;
    return get()?.analytics === true;
  }

  /** The choice the dialog starts from: what is in force, saved or by default. */
  function effectiveChoice(): ConsentChoice {
    return {
      preferences: allows('preferences'),
      analytics: allows('analytics'),
      marketing: false,
    };
  }

  function getSnapshot(): ConsentSnapshot {
    const record = get();
    if (!snapshot || snapshot.record !== record || snapshot.region !== region) {
      snapshot = { record, region };
    }
    return snapshot;
  }

  function notify(): void {
    const state = get();
    for (const listener of [...listeners]) listener(state);
  }

  /** Moves the remembered preferences between the two storages when that answer changes. */
  function movePreferences(toLocal: boolean): void {
    const local = safe(env.local);
    if (!local) return;
    const session = safe(env.session) ?? (memory ??= memoryStorage());
    const from = toLocal ? session : local;
    const to = toLocal ? local : session;
    const keys: string[] = [];
    safe(() => {
      for (let index = 0; index < from.length; index += 1) {
        const key = from.key(index);
        if (
          key &&
          key.startsWith(PREFERENCE_PREFIX) &&
          !NECESSARY_LOCAL_KEYS.includes(key) &&
          !SESSION_ONLY_KEYS.includes(key)
        ) {
          keys.push(key);
        }
      }
    });
    for (const key of keys) {
      // A grant copies what this tab remembered into localStorage so nothing
      // already dismissed comes back; a withdrawal moves it the other way and
      // deletes the persistent copy.
      safe(() => {
        const value = from.getItem(key);
        if (value !== null) to.setItem(key, value);
        if (!toLocal) from.removeItem(key);
      });
    }
  }

  function set(choice: ConsentChoice): ConsentState {
    const hadPreferences = allows('preferences');
    const state: ConsentState = {
      v: CONSENT_VERSION,
      necessary: true,
      preferences: choice.preferences,
      analytics: choice.analytics,
      // Nothing is offered under marketing today, and GPC rules it out for good.
      marketing: choice.marketing && !region?.gpc,
      at: new Date(env.now()).toISOString(),
    };
    const value = serializeConsent(state);
    safe(() => env.writeCookie(value, Math.floor(CONSENT_MAX_AGE_MS / 1000)));
    safe(() => env.local()?.setItem(CONSENT_MIRROR_KEY, value));

    if (state.preferences && !hadPreferences) movePreferences(true);
    if (!state.preferences) movePreferences(false);

    cached = state;
    const withdrawnAfterRunning = OPTIONAL_CATEGORIES.some(
      (category) => category !== 'preferences' && !state[category] && ran.has(category),
    );
    notify();
    if (withdrawnAfterRunning) env.reload();
    return state;
  }

  /**
   * The answer from `/api/geo` (or strict, when it failed). Set once per page;
   * a second call is ignored so nothing can loosen the model after the fact.
   */
  function setRegion(next: PrivacyRegion): void {
    if (region) return;
    const hadPreferences = allows('preferences');
    region = { regime: next.regime, gpc: next.gpc, country: next.country };
    // A default that allows preferences (the US model) carries over what this
    // tab already remembered, exactly as a yes in the dialog would.
    if (!hadPreferences && allows('preferences')) movePreferences(true);
    notify();
  }

  function subscribe(listener: (state: ConsentState | null) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  /**
   * Runs `run` once, as soon as `category` is allowed: now if it already is,
   * otherwise the moment it becomes so. Returns a cancel.
   */
  function whenAllowed(category: OptionalCategory, run: () => void): () => void {
    let done = false;
    const fire = () => {
      if (done || !allows(category)) return;
      done = true;
      ran.add(category);
      unsubscribe();
      run();
    };
    const unsubscribe = subscribe(fire);
    fire();
    return () => {
      done = true;
      unsubscribe();
    };
  }

  /** Answered, or in a region that never asks: the banner is out of the way. */
  function settled(): boolean {
    if (get()) return true;
    return policy() !== null && !needsAnswer();
  }

  /**
   * Waits until the banner is out of the way: any answer, yes or no, or a
   * region that never asks. Used by things that must not compete with it.
   */
  function whenAnswered(run: () => void): () => void {
    if (settled()) {
      run();
      return () => undefined;
    }
    const unsubscribe = subscribe(() => {
      if (!settled()) return;
      unsubscribe();
      run();
    });
    return unsubscribe;
  }

  /** Asks the banner to open the preferences dialog (the footer links and the account menu). */
  function requestOpen(): void {
    for (const listener of [...openListeners]) listener();
  }

  function onOpenRequest(listener: () => void): () => void {
    openListeners.add(listener);
    return () => openListeners.delete(listener);
  }

  /**
   * Where a preference that outlives the page is kept: localStorage once
   * preferences are allowed, sessionStorage until then, and memory where the
   * browser refuses sessionStorage too. Throws only where localStorage is
   * allowed but refused, exactly as reaching for `localStorage` would, so the
   * callers' existing try/catch keeps working unchanged.
   */
  function preferenceStorage(): KeyValue {
    if (allows('preferences')) {
      const local = env.local();
      if (!local) throw new Error('storage unavailable');
      return local;
    }
    const session = safe(env.session);
    if (session) return session;
    memory ??= memoryStorage();
    return memory;
  }

  return {
    get,
    set,
    subscribe,
    allows,
    whenAllowed,
    whenAnswered,
    requestOpen,
    onOpenRequest,
    preferenceStorage,
    setRegion,
    region: () => region,
    needsAnswer,
    replayAllowed,
    effectiveChoice,
    getSnapshot,
    acceptAll: () => set({ preferences: true, analytics: true, marketing: false }),
    rejectAll: () => set({ preferences: false, analytics: false, marketing: false }),
  };
}

function safe<T>(run: () => T): T | null {
  try {
    return run();
  } catch {
    return null;
  }
}

function browserEnv(): ConsentEnv {
  return {
    readCookie: () => readCookieValue(document.cookie, CONSENT_COOKIE),
    writeCookie: (value, maxAgeSeconds) => {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        `${CONSENT_COOKIE}=${encodeURIComponent(value)}; Path=/; ` +
        `Max-Age=${Math.max(0, maxAgeSeconds)}; SameSite=Lax${secure}`;
    },
    local: () => (typeof localStorage === 'undefined' ? null : localStorage),
    session: () => (typeof sessionStorage === 'undefined' ? null : sessionStorage),
    now: () => Date.now(),
    reload: () => window.location.reload(),
  };
}

/** The page's one store. Creating it touches nothing; the browser is read on first use. */
export const consent: ConsentStore = createConsentStore(browserEnv());

/** Shorthand for the callers that only need somewhere to keep a preference. */
export function preferenceStorage(): KeyValue {
  return consent.preferenceStorage();
}

/**
 * Loads a third-party script once its category is allowed. The only sanctioned
 * way to add analytics or marketing code; returns a cancel for a page that
 * unmounts before the answer comes.
 */
export function loadScript(
  category: Exclude<OptionalCategory, 'preferences'>,
  src: string,
  attributes: Record<string, string> = {},
): () => void {
  return consent.whenAllowed(category, () => {
    if (document.querySelector(`script[src="${CSS.escape(src)}"]`)) return;
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset['consent'] = category;
    for (const [name, value] of Object.entries(attributes)) script.setAttribute(name, value);
    document.head.appendChild(script);
  });
}
